import { authClient } from "@/api/auth";
import { LegalFooter } from "@/components/legal-footer";
import { BackgroundDots } from "@msuite/ui/background";
import { Button } from "@msuite/ui/button";
import { AuthForm, FormSwitch, TextInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { Link } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { type SignInSchema, userSignInSchema } from "@msuite/utils/schemas";
import { Fragment } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

export function SignIn() {
	/** Form */
	const methods = useForm<SignInSchema>({
		resolver: resolver(userSignInSchema),
	});

	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();

	/** Functions */
	async function handleOnSubmit(json: SignInSchema) {
		try {
			await authClient.signIn.email(json, {
				onError: (context) => {
					toast.error(context.error.message);
				},
			});
		} catch (error) {
			console.error(error);
			toast.error((error as Error).message);
		}
	}

	function handleOnNavigateToSignUp() {
		navigate("/sign-up");
	}

	/** Render */
	return (
		<Fragment>
			<BackgroundDots />
			<div className="flex-center w-screen h-screen">
				<AuthForm onSubmit={methods.handleSubmit(handleOnSubmit)}>
					<AuthForm.Header />
					<AuthForm.Body>
						<TextInput
							isRequired
							label="E-Mail"
							placeholder="max@mustermann.de"
							control={methods.control}
							name="email"
							data-cy="email-input"
						/>
						<TextInput
							isRequired
							label={t("auth.password")}
							placeholder="Password"
							type="password"
							control={methods.control}
							name="password"
							data-cy="password-input"
						/>
						<FormSwitch
							control={methods.control}
							name="rememberMe"
							data-cy="remember-checkbox"
							width="full"
						>
							{t("auth.remember_me")}
						</FormSwitch>
					</AuthForm.Body>
					<AuthForm.Footer>
						<Button
							type="submit"
							variant="primary"
							isLoading={methods.formState.isSubmitting}
							size="sm"
							data-cy="sign-in-button"
						>
							{t("auth.signin")}
						</Button>
						<Button
							onClick={handleOnNavigateToSignUp}
							size="sm"
							icon={<Icon name="ArrowRight" />}
							iconPosition="right"
							data-cy="sign-up-link"
						>
							{t("auth.signup")}
						</Button>
						<Link
							href="/forgot-password"
							className="text-xs"
							isExternal
						>
							{t("auth.forgot_password")}
						</Link>
					</AuthForm.Footer>
				</AuthForm>
			</div>
			<LegalFooter />
		</Fragment>
	);
}
